import React, { Component } from "react";
import PropTypes from "prop-types";
import { TimeRange, TimeSeries } from "pondjs";
import {
  AreaChart,
  Charts,
  ChartContainer,
  ChartRow,
  YAxis,
  LineChart,
  Resizable,
  styler,
  TimeRangeMarker,
  EventMarker
} from "react-timeseries-charts";
import { isMobile } from "../../utils/deviceutils";
import LoadingIndicatorOverlay from "../LoadingIndicatorOverlay";
import ErrorOverlay from "../ErrorOverlay";
import * as sensorReadingUnit from "../../model/sensors/sensorReadingUnitKeys";
import EnergyChartTooltip from "./EnergyChartTooltip";

const actualsLineColor = "#3eb3d3";
const baselineColor = "#A2D9C9";
const alertColor = "#c92319";
const thresholdAlertThresholdLineColor = "#0c0c0c";
const thresholdAlertThresholdLineDasharray = "5,5";

const tooltipId = "energy-chart-tooltip";

class EnergyChartGraph extends Component {
  state = { isMobile: isMobile() };

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  getCurrentValuesAtTracker(actualSeries, baselineSeries) {
    if (!this.state.tracker) {
      return;
    }

    const trackerTime = new Date(this.state.tracker);

    const actualSeriesEntry = actualSeries.atTime(trackerTime);

    const actual = actualSeriesEntry
      ? !isNaN(actualSeriesEntry.get("actual"))
        ? actualSeriesEntry.get("actual")
        : undefined
      : undefined;

    const baselineSeriesEntry = baselineSeries.atTime(trackerTime);
    const baseline = baselineSeriesEntry
      ? baselineSeriesEntry.get("baseline")
      : undefined;

    const actualDeltaFromBaseline =
      baseline != null && actual != null && !isNaN(baseline) && !isNaN(actual)
        ? Math.round((100 * (actual - baseline)) / baseline)
        : undefined;

    const timestamp =
      actualSeriesEntry !== undefined
        ? actualSeriesEntry.timestamp()
        : new Date();

    return {
      actual,
      baseline,
      actualDeltaFromBaseline,
      time: timestamp
    };
  }

  getMaxValue(series, mapKey) {
    return Array.from(series.events()).reduce((cur, s) => {
      const value = s.data().get(mapKey);
      return value > cur ? value : cur;
    }, 0);
  }

  getMinValue(series, mapKey) {
    return Array.from(series.events()).reduce((cur, s) => {
      const value = s.data().get(mapKey);
      if (!value) return cur;

      return !cur || value < cur ? value : cur;
    }, 0);
  }

  handleResize = () => {
    this.setState({
      isMobile: isMobile()
    });
  };

  handleTrackerChanged = tracker => {
    this.setState({ tracker });
  };

  getMarkerEvent(currentValues) {
    const xPosition = currentValues.time;
    const yPosition = currentValues.actual;

    if (!xPosition || !yPosition) return null;

    return this.props.actualsSeries.atTime(xPosition);
  }

  render() {
    const timeRange = new TimeRange(this.props.from, this.props.effectiveTo);

    const actualsSeries = this.props.actualsSeries;
    const baselineSeries = this.props.baselineSeries;
    const actualAlertOverlaySeries = this.props.actualAlertOverlaySeries;
    const alertSeries = this.props.alertSeries;
    const lightsOverThresholdAlertSeries = this.props
      .lightsOverThresholdAlertSeries;
    const hvacOverThresholdAlertSeries = this.props
      .hvacOverThresholdAlertSeries;

    const maxValue = Math.max(
      this.getMaxValue(actualsSeries, "actual"),
      this.getMaxValue(baselineSeries, "baseline"),
      this.getMaxValue(
        lightsOverThresholdAlertSeries,
        "lightsOverThresholdAlertThreshold"
      ),
      this.getMaxValue(
        hvacOverThresholdAlertSeries,
        "hvacOverThresholdAlertThreshold"
      )
    );

    let minValue = 0;
    if (!this.props.useZeroMinYValue) {
      if (
        this.props.sensor &&
        this.props.sensor.unit.description === sensorReadingUnit.KWH
      ) {
        minValue = this.getMinValue(actualsSeries, "actual");
      }
    }

    let labelSize = 12;
    let axisHeight = 30;
    const actualsLineWidth = 2;
    const thresholdAlertThresholdLineWidth = 2;

    if (this.state.isMobile) {
      labelSize = 8;
      axisHeight = 70;
    }

    const style = styler([
      { key: "actual", color: actualsLineColor, width: actualsLineWidth },
      { key: "baseline", color: baselineColor },
      { key: "alertActual", color: alertColor, width: actualsLineWidth }
    ]);

    const zeroAlertMarkerStyle = {
      zeroAlertMarker: {
        normal: {
          stroke: alertColor,
          strokeWidth: 10,
          transform: "translateY(5px)"
        }
      }
    };

    const xAxisStyle = {
      axis: {
        axisColor: "#c0c0c0"
      },
      labels: {
        labelSize: { labelSize },
        labelWeight: 100
      },
      values: { valueSize: { labelSize } }
    };

    const seriesLabel = this.props.sensor && this.props.sensor.name;

    const currentValues =
      this.getCurrentValuesAtTracker(actualsSeries, baselineSeries) || {};
    const unit = !!this.props.values ? this.props.sensor.unit.description : "";

    return (
      <div
        className="fable:mb-4 cb-c-energy-chart__graph"
        data-tip
        data-for={tooltipId}
      >
        <svg
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="0"
          height="0"
        >
          <defs>
            <pattern
              id="pattern"
              patternUnits="userSpaceOnUse"
              width="427"
              height="317"
            >
              <g
                id="Artboard-2"
                stroke="none"
                strokeWidth="1"
                fill="none"
                fillRule="evenodd"
                fillOpacity="0.2"
              >
                <g
                  id="Group"
                  transform="translate(213.500000, 158.000000) rotate(-30.400000) translate(-213.500000, -158.000000) translate(-95.000000, -74.000000)"
                  fill="#979797"
                  fillRule="nonzero"
                >
                  <rect id="rect-1" x="0" y="0" width="617" height="12" />
                  <rect id="rect-2" x="0" y="24" width="617" height="12" />
                  <rect id="rect-3" x="0" y="48" width="617" height="12" />
                  <rect id="rect-4" x="0" y="72" width="617" height="12" />
                  <rect id="rect-5" x="0" y="96" width="617" height="12" />
                  <rect id="rect-6" x="0" y="120" width="617" height="12" />
                  <rect id="rect-7" x="0" y="144" width="617" height="12" />
                  <rect id="rect-8" x="0" y="168" width="617" height="12" />
                  <rect id="rect-9" x="0" y="192" width="617" height="12" />
                  <rect id="rect-10" x="0" y="216" width="617" height="12" />
                  <rect id="rect-11" x="0" y="240" width="617" height="12" />
                  <rect id="rect-12" x="0" y="264" width="617" height="12" />
                  <rect id="rect-13" x="0" y="288" width="617" height="12" />
                  <rect id="rect-14" x="0" y="312" width="617" height="12" />
                  <rect id="rect-15" x="0" y="336" width="617" height="12" />
                  <rect id="rect-16" x="0" y="360" width="617" height="12" />
                </g>
              </g>
            </pattern>
          </defs>
        </svg>

        {this.props.showSeriesLabel && <h5>{seriesLabel}</h5>}
        {this.props.isLoading && <LoadingIndicatorOverlay />}
        {this.props.loadingFailed && (
          <ErrorOverlay message="Error fetching data" />
        )}
        <Resizable>
          <ChartContainer
            timeRange={timeRange}
            onTimeRangeChanged={this.props.onTimeRangeChanged}
            onTrackerChanged={this.handleTrackerChanged}
            timeAxisStyle={xAxisStyle}
            timeAxisAngledLabels={this.state.isMobile}
            timeAxisHeight={axisHeight}
            enableDragZoom
            showGrid={true}
          >
            <ChartRow height={this.props.chartHeight}>
              <YAxis
                id="y"
                label={""}
                visible={true}
                min={minValue}
                max={maxValue}
                width={60}
                type="linear"
                format=",.1f"
                isInnerAxis={true}
                showGrid={true}
              />
              <Charts>
                <AreaChart
                  axis="y"
                  series={baselineSeries}
                  columns={{ up: ["baseline"] }}
                  style={style}
                  fillOpacity={0.25}
                />
                <LineChart
                  axis="y"
                  series={actualsSeries}
                  columns={["actual"]}
                  style={style}
                />
                <LineChart
                  axis="y"
                  series={actualAlertOverlaySeries}
                  columns={["alertActual"]}
                  style={style}
                />
                <LineChart
                  axis="y"
                  series={alertSeries}
                  columns={["zeroAlertMarker"]}
                  style={zeroAlertMarkerStyle}
                />
                <LineChart
                  axis="y"
                  series={lightsOverThresholdAlertSeries}
                  columns={["lightsOverThresholdAlertThreshold"]}
                  style={{
                    lightsOverThresholdAlertThreshold: {
                      normal: {
                        stroke: thresholdAlertThresholdLineColor,
                        strokeWidth: thresholdAlertThresholdLineWidth,
                        strokeDasharray: thresholdAlertThresholdLineDasharray
                      }
                    }
                  }}
                  visible={this.props.showLightsOverThresholdAlertThreshold}
                />
                <LineChart
                  axis="y"
                  series={hvacOverThresholdAlertSeries}
                  columns={["hvacOverThresholdAlertThreshold"]}
                  style={{
                    hvacOverThresholdAlertThreshold: {
                      normal: {
                        stroke: thresholdAlertThresholdLineColor,
                        strokeWidth: thresholdAlertThresholdLineWidth,
                        strokeDasharray: thresholdAlertThresholdLineDasharray
                      }
                    }
                  }}
                  visible={this.props.showHvacOverThresholdAlertThreshold}
                />
                {this.props.closedTimes.map(time => {
                  return (
                    <TimeRangeMarker
                      key={time}
                      timerange={time}
                      style={{ fill: "url(#pattern)" }}
                      width={0}
                      height={0}
                      timeScale={x => x}
                      visible={
                        this.props.showStoreClosedCheckbox &&
                        this.props.showStoreClosedOverlay
                      }
                    />
                  );
                })}
                <EventMarker
                  type="point"
                  axis="y"
                  event={this.getMarkerEvent(currentValues)}
                  column="actual"
                  markerRadius={2}
                  markerStyle={{ fill: "black" }}
                />
              </Charts>
            </ChartRow>
          </ChartContainer>
        </Resizable>
        <EnergyChartTooltip
          id={tooltipId}
          time={currentValues.time}
          actual={currentValues.actual}
          delta={currentValues.actualDeltaFromBaseline}
          unit={unit}
        />
      </div>
    );
  }
}

EnergyChartGraph.propTypes = {
  sensor: PropTypes.object,
  from: PropTypes.instanceOf(Date).isRequired,
  to: PropTypes.instanceOf(Date).isRequired,
  values: PropTypes.array,
  chartHeight: PropTypes.number.isRequired,
  showSeriesLabel: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onTimeRangeChanged: PropTypes.func.isRequired,
  closedTimes: PropTypes.array,
  showStoreClosedCheckbox: PropTypes.bool,
  showStoreClosedOverlay: PropTypes.bool,
  actualsSeries: PropTypes.instanceOf(TimeSeries),
  baselineSeries: PropTypes.instanceOf(TimeSeries),
  actualAlertOverlaySeries: PropTypes.instanceOf(TimeSeries),
  alertSeries: PropTypes.instanceOf(TimeSeries),
  lightsOverThresholdAlertSeries: PropTypes.instanceOf(TimeSeries),
  showLightsOverThresholdAlertThreshold: PropTypes.bool,
  hvacOverThresholdAlertSeries: PropTypes.instanceOf(TimeSeries),
  showHvacOverThresholdAlertThreshold: PropTypes.bool,
  useZeroMinYValue: PropTypes.bool
};

export default EnergyChartGraph;
